import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Container, Card, CardBody, Row, Col, Button, Badge, Spinner, Alert, Input, Collapse,
  Pagination, PaginationItem, PaginationLink,
} from "reactstrap";
import { Copy, Search, Send, SlidersHorizontal, UserPlus } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import KullaniciKart from "../../../Components/Kullanici/KullaniciKart";
import KullaniciFiltreleri from "../../../Components/Kullanici/KullaniciFiltreleri";
import {
  BOS_FILTRE, SAYFA_BOYU, SIRALAMALAR, aktifFiltreSayisi, listeDurumuOku, listeDurumunuYaz, listeParametreleri,
  topluAktivasyonParametreleri, topluSuzgecVar,
} from "../../../Components/Kullanici/kullaniciListe";
import { getKullaniciListesi, getUserGroups } from "../../../services/userService";
import { PERMISSION_KEYS, useYetki } from "../../../services/rbacService";
import { fuarService } from "../../../services/fuarService";
import UserFormModal from "./UserFormModal";
import TopluAktivasyonMaili from "./TopluAktivasyonMaili";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../../i18n";
import i18n from "../../../i18n";

const ARAMA_DEBOUNCE_MS = 400;
const FILTRE_DEBOUNCE_MS = 400;

/** Aktif sayfanın çevresinde en fazla 7 sayfa numarası (21 bin kullanıcı yüzlerce sayfa eder). */
function sayfaNumaralari(aktif, toplam) {
  const bas = Math.max(1, Math.min(aktif - 3, toplam - 6));
  const son = Math.min(toplam, bas + 6);
  return Array.from({ length: son - bas + 1 }, (_, i) => bas + i);
}

/**
 * Kullanıcılar (backlog #379, 2026-10-13) — Kişi Listesi'nin kart düzeninde, sunucu tarafında sayfalı.
 *
 * WAS (backlog #13, 2026-09-15): GET /users/staff'ın tamamını çeken, tarayıcıda aranıp sayfalanan şeritli tablo; Düzenle /
 * Sil / Yeni Kullanıcı Ekle düğmeleri. Şimdi: `GET /users/kullanici-listesi` (backend #378) — HER giriş (firma tarafı dahil,
 * ~21 bin); Filtreler paneli Yetki Yönetimi › Kullanıcılar'ın (#309) süzgeçleriyle aynı. Taraf düğmeleri (Tümü | Personel |
 * Firma Tarafı) ve kartın taraf şeridi Deniz'in 2026-10-18/26 kararıyla kalktı (#379 takibi, #545): denetmen tarafında yalnızca
 * tüm kullanıcılar görünür, Rol süzgeci yine daraltır.
 *
 * #545 (2026-10-26): "Yeni Kullanıcı Ekle" gerçek bir form (UserFormModal, mode="create") — kullanıcıyı oluşturur ve yeni
 * kullanıcının Kullanıcı Kartı'nı açar. WAS (#379 kararı 1, 2026-10-13): "kullanıcılar eski sistemde oluşturulur" bilgi penceresi,
 * çünkü sunucuda POST /users reddediliyor ve canlı giriş alanları PHP'den eziyordu; ikisi de kalktı (#523, #524). Düğme yalnızca
 * MANAGE_USER_ROLES sahibine çizilir. "Sil" yine yok (backend rol / fuar / firma bağlarını da siler): Kullanıcı Kartı'nda Pasifleştir var.
 * "Telefon / e-posta tekrarları" (#491) aynı telefon ya da e-postayı paylaşan kullanıcıların raporunu açar.
 *
 * #572 (2026-10-29): bir arama ya da süzgeç uygulanmışken (ve MANAGE_USER_ROLES varken) "Aktivasyon maili gönder" çıkar: listenin süzgecine uyanlara
 * yeni şifre bağlantılı aktivasyon maili (TopluAktivasyonMaili; önizleme → onay → ilerleme). Çalışan işin numarası adreste (`?aktivasyonIs=`) durur.
 *
 * Arama, sıralama, süzgeç ve sayfa adres çubuğunda tutulur (#262 deseni) — Kullanıcı Kartı'ndan dönüşte yerinde durur.
 */
const Users = () => {
  const { t: tx } = useTranslation("yonetim");
  const { t: tc } = useTranslation("components");
  const yazabilir = useYetki(PERMISSION_KEYS.MANAGE_USER_ROLES) === true;
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { document.title = i18n.t("yonetim:usersIndex.kullanicilarFds"); }, []);

  // Liste durumu adres çubuğundan YALNIZCA açılışta okunur; sonrasında state asıl, URL onun yansıması.
  const [ilk] = useState(() => listeDurumuOku(searchParams));
  // Karttan dönüşte kaydırma konumu (Link state'i ya da tarayıcı geri tuşu için bu girdinin kendi state'i).
  const bekleyenKaydirma = useRef(typeof location.state?.scrollY === "number" ? location.state.scrollY : null);

  const [aramaInput, setAramaInput] = useState(ilk.arama);
  const [arama, setArama] = useState(ilk.arama);
  const [sirala, setSirala] = useState(ilk.sirala);
  // `filtre` panelde düzenlenen, `uygulananFiltre` debounce sonrası isteğe giden (ve URL'ye yazılan) hali.
  const [filtre, setFiltre] = useState(ilk.filtre);
  const [uygulananFiltre, setUygulananFiltre] = useState(ilk.filtre);
  const [filtreAcik, setFiltreAcik] = useState(() => aktifFiltreSayisi(ilk.filtre) > 0);
  const [page, setPage] = useState(ilk.sayfa);

  const [veri, setVeri] = useState({ users: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roller, setRoller] = useState([]);
  const [fuarlar, setFuarlar] = useState([]);
  const [olusturAcik, setOlusturAcik] = useState(false);
  // #572: toplu aktivasyon maili penceresi; çalışan işin numarası adreste durur (sayfa yenilense de aynı işe bağlanır)
  const [topluAcik, setTopluAcik] = useState(false);
  const [yenileSayac, setYenileSayac] = useState(0);
  const aktivasyonIs = searchParams.get("aktivasyonIs") || "";

  const aktifSayi = aktifFiltreSayisi(filtre);
  // Toplu postanın süzgeci = listede UYGULANMIŞ olan (Filtreler panelinde henüz uygulanmamış değişiklik sayılmaz).
  const topluParametreler = useMemo(() => topluAktivasyonParametreleri({ arama, filtre: uygulananFiltre }), [arama, uygulananFiltre]);
  const topluOnerilir = topluSuzgecVar({ arama, filtre: uygulananFiltre });

  function aktivasyonIsiYaz(id) {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("aktivasyonIs", id); else next.delete("aktivasyonIs");
    setSearchParams(next, { replace: true });
  }

  // Süzgeç seçicileri (rol, fuar): bir kez, hata sessiz — seçici boş kalır, liste yine çalışır.
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const g = await getUserGroups();
        if (!ignore) setRoller((g || []).map((x) => ({ id: x.id, ad: x.userGroupName })));
      } catch {
        if (!ignore) setRoller([]);
      }
      try {
        const f = await fuarService.getSelectableFuarList();
        if (!ignore) setFuarlar((f || []).map((x) => ({ fuarId: x.fuarId, ad: x.fuarName || x.fuarShortName || `#${x.fuarId}` })));
      } catch {
        if (!ignore) setFuarlar([]);
      }
    }
    run();
    return () => { ignore = true; };
  }, []);

  // Durumu adres çubuğuna yansıt (replace: geri tuşu her tuş vuruşunda bir adım gitmesin). Yalnızca UYGULANMIŞ değerler yazılır.
  useEffect(() => {
    const next = listeDurumunuYaz(searchParams, { arama, sirala, sayfa: page, filtre: uygulananFiltre });
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [arama, sirala, page, uygulananFiltre, searchParams, setSearchParams]);

  // Arama: 400 ms bekle, tek harfle istek atma. Değer zaten uygulanmışsa dokunma — yoksa adresten gelen sayfa 1'e sıfırlanırdı.
  const uygulananArama = useRef(arama);
  useEffect(() => { uygulananArama.current = arama; }, [arama]);
  useEffect(() => {
    const t = setTimeout(() => {
      const v = aramaInput.trim();
      if (v.length === 1 || v === uygulananArama.current) return;
      setArama(v);
      setPage(1);
    }, ARAMA_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [aramaInput]);

  // Süzgeçler: her tıklamada istek atma.
  useEffect(() => {
    if (filtre === uygulananFiltre) return undefined;
    const t = setTimeout(() => {
      setUygulananFiltre(filtre);
      setPage(1);
    }, FILTRE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filtre, uygulananFiltre]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const res = await getKullaniciListesi(listeParametreleri({ arama, sirala, sayfa: page, filtre: uygulananFiltre }));
        if (ignore) return;
        const toplam = res?.total ?? 0;
        const sonSayfa = Math.max(1, Math.ceil(toplam / SAYFA_BOYU));
        // Adresteki sayfa veri azaldığı için artık yoksa son sayfaya in (boş "Kullanıcı bulunamadı" çıkmasın).
        if (toplam > 0 && page > sonSayfa) { setPage(sonSayfa); return; }
        setVeri({ users: res?.users || [], total: toplam });
      } catch (e) {
        if (!ignore) { setError(e.message || i18n.t("yonetim:usersIndex.kullanicilarYuklenemedi")); setVeri({ users: [], total: 0 }); }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [arama, sirala, page, uygulananFiltre, yenileSayac]);

  // Karttan dönüşte liste yüklenip çizildikten sonra kaldığı yere kaydır — bir kez.
  useEffect(() => {
    if (loading || veri.users.length === 0 || bekleyenKaydirma.current == null) return;
    const y = bekleyenKaydirma.current;
    bekleyenKaydirma.current = null;
    // html { scroll-behavior: smooth } (bootstrap) olduğu için "instant": geri dönüşte yukarıdan kayarak inmesin.
    requestAnimationFrame(() => window.scrollTo({ top: y, left: 0, behavior: "instant" }));
  }, [loading, veri.users.length]);

  const rows = veri.users;
  const total = veri.total;
  const totalPages = Math.max(1, Math.ceil(total / SAYFA_BOYU));
  const gecerliSayfa = Math.min(page, totalPages);

  function temizleFiltre() {
    setFiltre(BOS_FILTRE);
    setUygulananFiltre(BOS_FILTRE);
    setPage(1);
  }

  // Karta giderken liste adresi + kaydırma konumu "geri" için bırakılır. Konum bu girdinin KENDİ state'ine de yazılır:
  // tarayıcı geri tuşu Link state'ini taşımaz ama girdiyi geri getirir.
  function openDetay(userId, ekstra) {
    const geri = `${location.pathname}${location.search}`;
    const scrollY = Math.round(window.scrollY);
    navigate(geri, { replace: true, state: { scrollY } });
    navigate(`/Users/${userId}`, { state: { from: geri, fromLabel: tx("usersIndex.kullanicilaraDon"), scrollY, ...ekstra } });
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("usersIndex.kullanicilar")} />

        <Card className="mb-3">
          <CardBody>
            <Row className="g-2 align-items-center">
              <Col lg={4} md={6}>
                <div className="position-relative">
                  <Search size={14} className="position-absolute text-muted" style={{ left: 12, top: 12 }} />
                  <Input
                    className="ps-5" value={aramaInput} placeholder={tx("usersIndex.adKullaniciAdiEPosta")}
                    onChange={(e) => setAramaInput(e.target.value)}
                  />
                </div>
              </Col>
              <Col lg="auto" md="auto">
                <Input type="select" value={sirala} onChange={(e) => { setSirala(e.target.value); setPage(1); }} aria-label={tx("usersIndex.sirala")}>
                  {SIRALAMALAR.map((x) => <option key={x.key} value={x.key}>{tc(x.etiketKey)}</option>)}
                </Input>
              </Col>
              <Col lg="auto" md="auto">
                <Button color="light" className="text-nowrap" active={filtreAcik} onClick={() => setFiltreAcik((a) => !a)}>
                  <SlidersHorizontal size={15} className="me-1" />{tx("usersIndex.filtreler")}
                  {aktifSayi > 0 && <Badge color="primary" pill className="ms-2">{aktifSayi}</Badge>}
                </Button>
              </Col>
              <Col className="d-flex gap-2 justify-content-lg-end flex-wrap">
                <Button tag={Link} to="/Users/IletisimTekrarlari" color="light" className="text-nowrap">
                  <Copy size={15} className="me-1" />{tx("usersIndex.iletisimTekrarlari")}
                </Button>
                {yazabilir && topluOnerilir && (
                  <Button color="light" className="text-nowrap border" onClick={() => setTopluAcik(true)}>
                    <Send size={15} className="me-1" />{tx("usersIndex.aktivasyonMailiGonder")}
                  </Button>
                )}
                {yazabilir && (
                  <Button color="success" className="text-nowrap" onClick={() => setOlusturAcik(true)}>
                    <UserPlus size={15} className="me-1" />{tx("usersIndex.yeniKullaniciEkle")}
                  </Button>
                )}
              </Col>
            </Row>

            <Collapse isOpen={filtreAcik}>
              <div className="border-top mt-3 pt-3">
                <KullaniciFiltreleri
                  filtre={filtre} onChange={setFiltre} roller={roller} fuarlar={fuarlar}
                  aktifSayi={aktifSayi} onTemizle={temizleFiltre}
                />
              </div>
            </Collapse>
          </CardBody>
        </Card>

        {error && <Alert fade={false} color="danger" toggle={() => setError(null)}>{error}</Alert>}

        <div className="d-flex align-items-center justify-content-between mb-2 px-1">
          <h6 className="mb-0">
            {tx("usersIndex.kullanicilar")}
            {!loading && <Badge color="light" className="text-body border fw-normal ms-2">{total.toLocaleString(currentLocaleTag())}</Badge>}
          </h6>
          {loading && rows.length > 0 && <Spinner size="sm" color="primary" />}
        </div>

        {loading && rows.length === 0 && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">{tx("common:yukleniyor")}</p>
          </div>
        )}

        {!loading && rows.length === 0 && !error && <Alert fade={false} color="secondary">{tx("usersIndex.kullaniciBulunamadi")}</Alert>}

        {rows.map((u) => (
          <KullaniciKart
            key={u.userId} u={u} soluk={loading}
            href={`/Users/${u.userId}`} onDetay={() => openDetay(u.userId)}
          />
        ))}

        {rows.length > 0 && (
          <div className="d-flex flex-column align-items-center p-3">
            {totalPages > 1 && (
              <Pagination>
                <PaginationItem disabled={gecerliSayfa <= 1}>
                  <PaginationLink onClick={() => setPage(1)}>{tx("usersIndex.ilk")}</PaginationLink>
                </PaginationItem>
                <PaginationItem disabled={gecerliSayfa <= 1}>
                  <PaginationLink previous onClick={() => setPage(gecerliSayfa - 1)} />
                </PaginationItem>
                {sayfaNumaralari(gecerliSayfa, totalPages).map((n) => (
                  <PaginationItem key={n} active={n === gecerliSayfa}>
                    <PaginationLink onClick={() => setPage(n)}>{n}</PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem disabled={gecerliSayfa >= totalPages}>
                  <PaginationLink next onClick={() => setPage(gecerliSayfa + 1)} />
                </PaginationItem>
                <PaginationItem disabled={gecerliSayfa >= totalPages}>
                  <PaginationLink onClick={() => setPage(totalPages)}>{tx("usersIndex.son")}</PaginationLink>
                </PaginationItem>
              </Pagination>
            )}
            <div className="text-muted small">
              {tx("usersIndex.sayfaBilgisi", {
                sayfa: gecerliSayfa, toplamSayfa: totalPages.toLocaleString(currentLocaleTag()), toplam: total.toLocaleString(currentLocaleTag()),
              })}
            </div>
          </div>
        )}
      </Container>

      {yazabilir && (topluAcik || aktivasyonIs) && (
        <TopluAktivasyonMaili
          parametreler={topluParametreler} isId={aktivasyonIs} onIsId={aktivasyonIsiYaz}
          onKapat={(yenile) => { setTopluAcik(false); if (yenile) setYenileSayac((n) => n + 1); }}
        />
      )}

      {olusturAcik && (
        <UserFormModal
          mode="create"
          onClose={(sonuc) => {
            setOlusturAcik(false);
            if (sonuc) openDetay(sonuc.userId, { info: tx("usersIndex.kullaniciOlusturuldu") });
          }}
        />
      )}
    </div>
  );
};

export default Users;
